/**
 * Investor rate-sheet uploads for a price source (PriceSourceUploadsController on Skynet.Pricing).
 * Same FeedAdminPolicy gate as PriceSourcesApi, so every call skips the global forbidden-reload and
 * a missing claim surfaces inline through the calling action.
 */
import { SKIP_FORBIDDEN } from './httpConfig';

const MULTIPART_HEADERS = { 'Content-Type': 'multipart/form-data' };

export class PriceSourceUploadsApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async uploadFile({ priceSourceId, file, effectiveDate = null, uploadType = null }) {
    const formData = new FormData();

    formData.append('file', file);

    if (effectiveDate) {
      formData.append('effectiveDate', effectiveDate);
    }

    if (uploadType) {
      formData.append('uploadType', uploadType);
    }

    return this.#http.post(`/api/price-sources/${priceSourceId}/uploads`, formData, {
      ...SKIP_FORBIDDEN,
      headers: MULTIPART_HEADERS,
    });
  }

  async getUploads(priceSourceId, { take } = {}) {
    return this.#http.get(`/api/price-sources/${priceSourceId}/uploads`, {
      ...SKIP_FORBIDDEN,
      params: { take },
    });
  }

  async getUpload(uploadId) {
    return this.#http.get(`/api/price-source-uploads/${uploadId}`, SKIP_FORBIDDEN);
  }

  async getFile(uploadId) {
    return this.#http.get(`/api/price-source-uploads/${uploadId}/file`, {
      ...SKIP_FORBIDDEN,
      responseType: 'blob',
    });
  }

  async getGrid(uploadId, gridId) {
    return this.#http.get(
      `/api/price-source-uploads/${uploadId}/grids/${encodeURIComponent(gridId)}`,
      SKIP_FORBIDDEN,
    );
  }

  async getProposedVersion(uploadId) {
    return this.#http.get(`/api/price-source-uploads/${uploadId}/proposed-version`, SKIP_FORBIDDEN);
  }

  async getProgramCandidates(uploadId) {
    return this.#http.get(
      `/api/price-source-uploads/${uploadId}/program-candidates`,
      SKIP_FORBIDDEN,
    );
  }

  async updateProposal(uploadId, body) {
    return this.#http.put(`/api/price-source-uploads/${uploadId}/proposal`, body, SKIP_FORBIDDEN);
  }

  async getWorkbook(uploadId) {
    return this.#http.get(`/api/price-source-uploads/${uploadId}/workbook`, SKIP_FORBIDDEN);
  }

  async updateExtraction(uploadId, body) {
    return this.#http.put(`/api/price-source-uploads/${uploadId}/extraction`, body, SKIP_FORBIDDEN);
  }

  // LEN-259 phase 7.2: a closed-set upload's outline (tabs, headings, blocks and the labels to tag);
  // 404 when the upload has none. sheet, a tab's index or name, keeps only that tab.
  async getUploadOutline(uploadId, sheet = null) {
    return this.#http.get(`/api/price-source-uploads/${uploadId}/outline`, {
      ...SKIP_FORBIDDEN,
      params: { sheet: sheet ?? undefined },
    });
  }

  // The outcome gate's review of the reading: the applicability matrix, invariant firings, siblings,
  // probe totals, the not-priced list and what needs a person. With sheet, also that tab's coverage
  // map, a page at a time (continuation reads on).
  async getUploadReview(uploadId, sheet = null, continuation = null) {
    return this.#http.get(`/api/price-source-uploads/${uploadId}/review`, {
      ...SKIP_FORBIDDEN,
      params: { sheet: sheet ?? undefined, continuation: continuation ?? undefined },
    });
  }

  // A page of a tab's cells as row text ("r12: B=6.125 | C=100.250"), at most 4,000 cells;
  // continuation reads on. range is A1:T80, B12, B:F or 12:40.
  async getUploadCells(uploadId, sheet, range = null, continuation = null) {
    return this.#http.get(`/api/price-source-uploads/${uploadId}/cells`, {
      ...SKIP_FORBIDDEN,
      params: {
        sheet,
        range: range ?? undefined,
        continuation: continuation ?? undefined,
      },
    });
  }

  // A partial closed-set reading (UploadReadingWrite): tabs merge by block, label and added range,
  // and scope overrides by grid key.
  async saveUploadReading(uploadId, reading) {
    return this.#http.put(`/api/price-source-uploads/${uploadId}/reading`, reading, SKIP_FORBIDDEN);
  }

  async startAiReading(uploadId, gridKeys) {
    return this.#http.post(
      `/api/price-source-uploads/${uploadId}/ai-reading`,
      gridKeys?.length ? { gridKeys } : null,
      SKIP_FORBIDDEN,
    );
  }

  async readWithParser(uploadId) {
    return this.#http.post(
      `/api/price-source-uploads/${uploadId}/parser-reading`,
      null,
      SKIP_FORBIDDEN,
    );
  }

  async submit(uploadId, body) {
    return this.#http.post(`/api/price-source-uploads/${uploadId}/submit`, body, SKIP_FORBIDDEN);
  }

  async approve(uploadId, body) {
    return this.#http.post(`/api/price-source-uploads/${uploadId}/approve`, body, SKIP_FORBIDDEN);
  }

  async discard(uploadId) {
    return this.#http.post(`/api/price-source-uploads/${uploadId}/discard`, null, SKIP_FORBIDDEN);
  }
}
